import { useState } from "react";
import { FolderPlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, safe } from "@/lib/api";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

export const DTONE = {
  Allow: "bg-emerald-50 text-emerald-700 ring-emerald-200", "Hold for review": "bg-amber-50 text-amber-700 ring-amber-200",
  Release: "bg-emerald-50 text-emerald-700 ring-emerald-200", "Standard availability": "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Hold: "bg-amber-50 text-amber-700 ring-amber-200", "Hold / review": "bg-amber-50 text-amber-700 ring-amber-200", Escalate: "bg-violet-50 text-violet-700 ring-violet-200",
  "Apply exception hold with customer notice": "bg-amber-50 text-amber-700 ring-amber-200",
  Block: "bg-red-50 text-red-700 ring-red-200", "Suspend origination": "bg-red-50 text-red-700 ring-red-200", "Verify with paying bank before release": "bg-red-50 text-red-700 ring-red-200",
};

export const Pill = ({ d, id }) => <span data-testid={id} className={`chip border-0 ring-1 ${DTONE[d] || "bg-slate-50 text-slate-700 ring-slate-200"}`}>{d}</span>;

export function DecisionBox({ kind, item, choices, onData }) {
  const [d, setD] = useState(item.engine_decision);
  const [why, setWhy] = useState("");
  const submit = safe(async () => {
    const r = await api.post(`/aml/${kind}/${item.id}/decision`, { decision: d, rationale: why });
    onData(r.data); setWhy("");
    toast.success(`Decision recorded: ${d}`);
  });
  const a = item.analyst;
  const { persona } = useApp();
  const can = persona?.permissions.includes("wire.act");
  return (
    <div className="panel p-4 space-y-3" data-testid={`aml-decision-${item.id}`}>
      <div className="flex flex-wrap justify-between gap-2 items-center">
        <div className="text-sm font-semibold text-slate-900">Human decision</div>
        <div className="text-[11px] text-slate-500">Engine: <Pill d={item.engine_decision} id={`aml-engine-${item.id}`} /> · {item.policy_version}</div>
      </div>
      {a && (
        <div data-testid={`aml-analyst-${item.id}`} className="rounded-lg bg-slate-50 ring-1 ring-slate-200 p-3 text-xs">
          <div className="flex gap-2 items-center"><Pill d={a.decision} id={`aml-analyst-decision-${item.id}`} />{a.override && <span className="chip border-0 ring-1 ring-amber-200 bg-amber-50 text-amber-700">Override of engine</span>}</div>
          <div className="mt-1.5 text-slate-700">{a.by} · {new Date(a.at).toLocaleString("en-US")}</div>
          <div className="mt-1 italic text-slate-800">{a.rationale}</div>
        </div>
      )}
      {item.history?.length > 1 && <div className="text-[11px] text-slate-500 space-y-0.5" data-testid={`aml-history-${item.id}`}><div className="eyebrow">Decision history</div>{item.history.map((h, i) => <div key={i}>{new Date(h.at).toLocaleString("en-US")} · {h.previous || "-"} → <b>{h.decision}</b> · {h.by}</div>)}</div>}
      {can && <OpenCase kind={kind} item={item} />}
      {!can && <div className="text-[11px] text-slate-500" data-testid={`aml-no-permission-${item.id}`}>View only: recording AML decisions needs wire operations or manager access.</div>}
      {can && <><div className="flex flex-wrap gap-1.5">
        {choices.map((c) => (
          <button key={c} data-testid={`aml-choice-${item.id}-${c.split(" ")[0].toLowerCase()}`} onClick={() => setD(c)}
            className={`text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border transition-colors ${d === c ? "bg-sky-600 text-white border-sky-600" : "border-slate-200 text-slate-600 hover:border-sky-300"}`}>{c}</button>
        ))}
      </div>
      {d !== item.engine_decision && <div className="text-[11px] text-amber-700">This differs from the engine decision and will be recorded as an override.</div>}
      <div className="flex gap-2">
        <input data-testid={`aml-rationale-${item.id}`} value={why} onChange={(e) => setWhy(e.target.value)} placeholder="Rationale (min 10 characters)" className="flex-1 text-xs px-3 py-1.5 rounded-lg border border-slate-200 outline-none focus:border-sky-400" />
        <button data-testid={`aml-submit-${item.id}`} disabled={why.trim().length < 10} onClick={submit} className="btn-primary disabled:opacity-40">Record</button>
      </div></>}
    </div>
  );
}

function OpenCase({ kind, item }) {
  const nav = useNavigate();
  const open = safe(async () => { const r = await api.post("/aml/cases", { kind, item_ids: [item.id], typology: kind === "wallets" ? "Digital-asset exposure" : kind === "ach" ? "Nested ACH / third-party sender" : kind === "checks" ? "Check fraud" : "Nested correspondent banking" }); toast.success(`Case ${r.data.id} opened`); nav(`/aml?tab=cases&case=${r.data.id}`); });
  return <button data-testid={`aml-open-case-${item.id}`} onClick={open} className="btn-ghost text-[11px]"><FolderPlus className="w-3.5 h-3.5" />Open investigation case</button>;
}

export function ItemList({ items, sel, setSel, render }) {
  return (
    <div className="space-y-2">
      {items.map((i) => (
        <button key={i.id} data-testid={`aml-item-${i.id}`} onClick={() => setSel(i.id)}
          className={`w-full text-left p-3 rounded-xl border transition-colors ${sel === i.id ? "bg-white border-sky-300 shadow-[0_1px_4px_rgba(2,132,199,0.15)]" : "bg-white/60 border-slate-200 hover:bg-white"}`}>
          {render(i)}
        </button>
      ))}
    </div>
  );
}
